import React, { createContext, useCallback, useContext, useState } from 'react';

type ComplementItem = {
  id: number | string;
  name?: string;
  order: number;
  rawMaterialId?: number;
  maximumQuantity: number;
  minimumQuantity: number;
  image?: File | string;
  price: number;
  pauseSale?: boolean;
};

type Complement = {
  id?: number | string;
  name?: string;
  order?: number;
  maximumQuantity?: number;
  minimumQuantity?: number;
  isRequired?: boolean;
  items?: ComplementItem[];
  pauseSale?: boolean;
};

export type Grid = {
  id: number | string;
  name: string;
  order: number;
  image?: File | string;
  minimumStock: number;
  currentStock: number;
  price: number;
  discount: number;
  numberOfSlices: number;
  numberOfFlavors: number;
  sizeId: number;
  code?: string;
  active: boolean;
  canChangeCurrentStock: boolean;
};

type Option = {
  value: number;
  label: string;
};

export type AvailabilityDetails = {
  days: {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
  };
  shifts: {
    startTime: number;
    endTime: number;
  }[];
};

export type Image = {
  id?: number;
  file?: File | string;
};

type Product = {
  id?: number;
  image?: File | string;
  name?: string;
  gtin?: string;
  images: Array<Image | undefined>;
  groupId?: number;
  observation?: string;
  complements?: Complement[];
  grids: Grid[];
  isAwaysAvailability?: boolean;
  unitOfMeasurementId?: number;
  details: AvailabilityDetails;
  isCloning: boolean;
  display?: boolean;
};

type ISize = {
  id: number;
  name: string;
  order: number;
  numberOfFlavors: number;
};

type PizzaProductContextData = Product & {
  groupsOptions: Option[];
  isDrawerOpened?: boolean;
  complementsDrawerTab: 0 | 1;
  currentTab: 0 | 1 | 2 | 3 | 4;
  unitsOfMeasurementOptions: Option[];
  isComplementsDrawerOpened?: boolean;
  selectedGrid?: number;
  numOfFlavors?: number;
  display: boolean;
  sizes: ISize[];
  handleChangeSizes(value: ISize[]): void;
  handleChangeDisplay(value: boolean): void;
  handleChangeSelectedNumOfFlavors(num: number): void;
  handleChangeSelectedGrid(id: number): void;
  handleAddShift(): void;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetProduct(): void;
  handleEditGrid(grid: Grid): void;
  handleChangeTab(tab: number): void;
  handleOpenComplementsDrawer(): void;
  handleCloseComplementsDrawer(): void;
  handleRemoveShift(shiftIndex: number): void;
  handleAddComplement(complement: Complement): void;
  handleEditComplement(complement: Complement): void;
  handleChangeComplementsDrawerTab(tab: 0 | 1): void;
  handleChangeGroupsOptions(options: Option[]): void;
  handleChangeImage(index: number, image?: File): void;
  handleDeleteComplement(complement: Complement): void;
  handleChangeProduct(partialProduct: Partial<Product>): void;
  handleChangeUnitsOfMeasurementOptions(options: Option[]): void;
  handleToggleCanChangeCurrentStock(gridId: string | number): void;
  handleChangeAvailabilityDays(days: AvailabilityDetails['days']): void;
  handleChangeShift(
    fieldName: keyof AvailabilityDetails['shifts'][0],
    value: number,
    shiftIndex: number,
  ): void;
};

const PizzaProductContext = createContext<PizzaProductContextData>(
  {} as PizzaProductContextData,
);

const INITIAL_PRODUCT_DATA = {
  isCloning: false,
  unitOfMeasurementId: 6,
  isAwaysAvailability: true,
  images: Array(6).fill(undefined),
  grids: [],
  details: {
    days: {
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
    },
    shifts: [],
  },
};

const PizzaProductProvider: React.FC = ({ children }) => {
  const [currentTab, setCurrentTab] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [sizes, setSizes] = useState<ISize[]>([]);
  const [complementsDrawerTab, setComplementsDrawerTab] = useState<0 | 1>(0);
  const [selectedGrid, setSelectedGrid] = useState<number>();
  const [numOfFlavors, setNumOfFlavors] = useState<number>();
  const [display, setDisplay] = useState(false);
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean | undefined>(
    undefined,
  );
  const [isComplementsDrawerOpened, setIsComplementsDrawerOpened] = useState<
    boolean | undefined
  >(undefined);
  const [product, setProduct] = useState<Product>(INITIAL_PRODUCT_DATA);
  const [unitsOfMeasurementOptions, setUnitsOfMeasurementOptions] = useState<
    Option[]
  >([]);
  const [groupsOptions, setGroupsOptions] = useState<Option[]>([]);

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleOpenComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(true);
  }, []);

  const handleCloseComplementsDrawer = useCallback(() => {
    setIsComplementsDrawerOpened(false);
  }, []);

  const handleChangeGroupsOptions = useCallback((options: Option[]) => {
    setGroupsOptions(options);
  }, []);

  const handleChangeUnitsOfMeasurementOptions = useCallback(
    (options: Option[]) => {
      setUnitsOfMeasurementOptions(options);
    },
    [],
  );

  const handleChangeSelectedGrid = useCallback((gridId: number) => {
    setNumOfFlavors(undefined);
    setSelectedGrid(gridId);
  }, []);

  const handleChangeSelectedNumOfFlavors = useCallback((num: number) => {
    setNumOfFlavors(num);
  }, []);

  const handleChangeDisplay = useCallback((value: boolean) => {
    setDisplay(value);
  }, []);

  const handleChangeSizes = useCallback((value: ISize[]) => {
    setSizes(value);
  }, []);

  const handleAddComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: [...(oldState?.complements || []), complement],
    }));
  }, []);

  const handleDeleteComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).filter(
        (oldComplement) => oldComplement.id !== complement.id,
      ),
    }));
  }, []);

  const handleEditComplement = useCallback((complement: Complement) => {
    setProduct((oldState) => ({
      ...oldState,
      complements: (oldState?.complements || []).map((oldComplement) => {
        if (oldComplement.id === complement.id) {
          return complement;
        }

        return oldComplement;
      }),
    }));
  }, []);

  const handleEditGrid = useCallback((grid: Grid) => {
    setProduct((oldState) => ({
      ...oldState,
      grids: oldState.grids.map((oldGrid) => {
        if (oldGrid.id === grid.id) {
          return grid;
        }

        return oldGrid;
      }),
    }));
  }, []);

  const handleToggleCanChangeCurrentStock = useCallback(
    (gridId: string | number) => {
      setProduct((oldState) => ({
        ...oldState,
        grids: oldState.grids.map((oldGrid) => {
          if (oldGrid.id === gridId) {
            return {
              ...oldGrid,
              canChangeCurrentStock: !oldGrid.canChangeCurrentStock,
            };
          }

          return oldGrid;
        }),
      }));
    },
    [],
  );

  const handleChangeProduct = useCallback(
    (partialProduct: Partial<Product>) => {
      setProduct((oldState) => ({ ...oldState, ...partialProduct }));
    },
    [],
  );

  const handleResetProduct = useCallback(() => {
    setProduct(INITIAL_PRODUCT_DATA);
    setSelectedGrid(undefined);
    setNumOfFlavors(undefined);
    setDisplay(false);
  }, []);

  const handleAddShift = useCallback(() => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: [...oldState.details.shifts, { startTime: 1, endTime: 23 }],
      },
    }));
  }, []);

  const handleChangeShift = useCallback(
    (
      fieldName: keyof AvailabilityDetails['shifts'][0],
      value: number,
      shiftIndex: number,
    ) => {
      setProduct((oldState) => ({
        ...oldState,
        details: {
          days: oldState.details.days,
          shifts: oldState.details.shifts.map((shift, index) => {
            if (shiftIndex === index) {
              return {
                ...shift,
                [fieldName]: value,
              };
            }

            return shift;
          }),
        },
      }));
    },
    [],
  );

  const handleRemoveShift = useCallback((shiftIndex: number) => {
    setProduct((oldState) => ({
      ...oldState,
      details: {
        days: oldState.details.days,
        shifts: oldState.details.shifts.filter(
          (_shift, index) => shiftIndex !== index,
        ),
      },
    }));
  }, []);

  const handleChangeTab = useCallback((tab: 0 | 1 | 2 | 3 | 4) => {
    setCurrentTab(tab);
  }, []);

  const handleChangeComplementsDrawerTab = useCallback((tab: 0 | 1) => {
    setComplementsDrawerTab(tab);
  }, []);

  const handleChangeAvailabilityDays = useCallback(
    (days: AvailabilityDetails['days']) => {
      setProduct((oldState) => ({
        ...oldState,
        details: { ...oldState.details, days },
      }));
    },
    [],
  );

  const handleChangeImage = useCallback(
    (index: number, imageToChange?: File) => {
      setProduct((oldState) => ({
        ...oldState,
        images: oldState.images.map((image, imageIndex) => {
          if (imageIndex === index) {
            if (!imageToChange && !image?.id) {
              return undefined;
            }

            return {
              ...image,
              file: imageToChange,
            };
          }

          return image;
        }),
      }));
    },
    [],
  );

  return (
    <PizzaProductContext.Provider
      value={{
        currentTab,
        groupsOptions,
        isDrawerOpened,
        complementsDrawerTab,
        isComplementsDrawerOpened,
        unitsOfMeasurementOptions,
        selectedGrid,
        numOfFlavors,
        display,
        sizes,
        handleChangeDisplay,
        handleChangeSizes,
        handleChangeSelectedGrid,
        handleChangeSelectedNumOfFlavors,
        handleEditGrid,
        handleAddShift,
        handleChangeTab,
        handleOpenDrawer,
        handleChangeShift,
        handleCloseDrawer,
        handleChangeImage,
        handleRemoveShift,
        handleResetProduct,
        handleChangeProduct,
        handleAddComplement,
        handleEditComplement,
        handleDeleteComplement,
        handleChangeGroupsOptions,
        handleOpenComplementsDrawer,
        handleChangeAvailabilityDays,
        handleCloseComplementsDrawer,
        handleChangeComplementsDrawerTab,
        handleToggleCanChangeCurrentStock,
        handleChangeUnitsOfMeasurementOptions,
        ...product,
      }}
    >
      {children}
    </PizzaProductContext.Provider>
  );
};

function usePizzaProduct(): PizzaProductContextData {
  const context = useContext(PizzaProductContext);

  if (!context)
    throw new Error('usePizzaProduct must be used within PizzaProductProvider');

  return context;
}

export { PizzaProductProvider, usePizzaProduct };
